// src/pages/drone/buah-alami/ReportBuahAlami.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Stack,
    Chip,
    Menu,
    MenuItem,
    Grid,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import ArrowDropDownOutlinedIcon from "@mui/icons-material/ArrowDropDownOutlined";
// Di bagian import ikon, tambahkan ini:
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { database } from "../../../firebase";
import { ref, onValue } from "firebase/database";

// Import CustomTableContainer
import CustomTableContainer from "../../../components/CustomScrollContainer";

// Import komponen view peta
import MapViewTooltip from "./components/MapViewTooltip";

// Import formatters dari file utilitas
import {
    formatNumber,
    formatDecimal,
    formatDateDisplay,
    formatText
} from "../../../utils/formatters-master";

// Import fungsi calculation formula untuk berbagai perhitungan
import {
    calculateUmurHSFSaatIni,
    calculateAllFormulas,
    calculateUmurTerbang
} from "./formula/calculationFormula";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper: Cari data lokasi berdasarkan id_lokasi
const findLokasiById = (lokasiArray, id_lokasi) => {
    if (!lokasiArray || !id_lokasi) return null;
    return lokasiArray.find((lokasi) => lokasi.id_lokasi === id_lokasi);
};

// Helper: Parse tanggal dengan berbagai format
const parseTanggalTerbang = (tanggalStr) => {
    if (!tanggalStr || tanggalStr === "-") return null;

    try {
        let day, month, year;

        // Cek format: DD-MM-YYYY
        if (tanggalStr.includes("-")) {
            const parts = tanggalStr.split("-");
            if (parts.length === 3) {
                day = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10);
                year = parseInt(parts[2], 10);
            }
        }
        // Cek format: DD/MM/YYYY
        else if (tanggalStr.includes("/")) {
            const parts = tanggalStr.split("/");
            if (parts.length === 3) {
                day = parseInt(parts[0], 10);
                month = parseInt(parts[1], 10);
                year = parseInt(parts[2], 10);
            }
        }

        // Validasi
        if (day && month && year && year >= 2000 && year <= 2100) {
            return { day, month, year };
        }
    } catch (error) {
        console.error("Error parsing tanggal:", tanggalStr, error);
    }

    return null;
};

// Helper: Konversi string angka dengan koma ke number
const parseNumberWithComma = (str) => {
    if (!str || str === "-" || str === "" || str === null || str === undefined) return 0;
    try {
        // Ganti koma dengan titik jika ada, lalu parseFloat
        const cleanedStr = String(str).replace(",", ".");
        const num = parseFloat(cleanedStr);
        return isNaN(num) ? 0 : num;
    } catch {
        return 0;
    }
};

// Helper: Format angka untuk tooltip (tanpa desimal)
const formatNumberForTooltip = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0";
    
    // Format angka tanpa desimal
    const formatted = Math.round(value).toLocaleString('id-ID');
    
    return formatted;
};

// Helper: Format persentase untuk tooltip
const formatPercentageForTooltip = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "0%";
    
    // Format persentase dengan 2 desimal
    const formatted = `${parseFloat(value).toFixed(2)}%`;
    
    return formatted;
};

// Helper: Konversi persentase string ke number untuk chart
const parsePercentageToNumber = (str) => {
    if (!str || str === "-" || str === "" || str === null || str === undefined) return 0;
    try {
        // Hapus tanda persen dan ubah koma ke titik
        const cleanedStr = String(str).replace("%", "").replace(",", ".");
        const num = parseFloat(cleanedStr);
        return isNaN(num) ? 0 : num;
    } catch {
        return 0;
    }
};

// Helper: Ambil Real Forcing dari Setara Urea Per Umur
const getRealForcingFromSetaraUrea = async (id_lokasi, database, onValuePromise) => {
    try {
        // Fetch data from db_plantation/tb_setara_urea_per_umur
        const setaraUreaRef = ref(database, "db_plantation/tb_setara_urea_per_umur");
        const setaraUreaSnapshot = await onValuePromise(setaraUreaRef);
        const setaraUreaData = setaraUreaSnapshot.val();
        
        if (!setaraUreaData) return null;
        
        // Cari data dengan id_lokasi yang sesuai
        let foundRealForcing = null;
        
        Object.keys(setaraUreaData).forEach(key => {
            const record = setaraUreaData[key];
            if (record.id_lokasi && record.id_lokasi.toString() === id_lokasi.toString()) {
                foundRealForcing = record.real_forcing || null;
            }
        });
        
        return foundRealForcing;
        
    } catch (error) {
        console.error("Error fetching real forcing from setara urea:", error);
        return null;
    }
};

// Helper: Ambil data Tanggal Closing dari Monitor Hasil Panen
const getTanggalClosingFromMonitorHasilPanen = async (id_lokasi, database, onValuePromise) => {
    try {
        // Fetch data from db_harvesting/tb_monitor_hasil_panen
        const monitorRef = ref(database, "db_harvesting/tb_monitor_hasil_panen");
        const monitorSnapshot = await onValuePromise(monitorRef);
        const monitorData = monitorSnapshot.val();
        
        if (!monitorData) return null;
        
        // Cari data dengan id_lokasi yang sesuai
        let foundTanggalClosing = null;
        
        Object.keys(monitorData).forEach(key => {
            const record = monitorData[key];
            if (record.id_lokasi && record.id_lokasi.toString() === id_lokasi.toString()) {
                foundTanggalClosing = record.tanggal_closing || null;
            }
        });
        
        return foundTanggalClosing;
        
    } catch (error) {
        console.error("Error fetching tanggal closing from monitor hasil panen:", error);
        return null;
    }
};

// Daftar nama bulan dalam Bahasa Indonesia
const bulanList = [
    { id: 1, nama: "Januari" },
    { id: 2, nama: "Februari" },
    { id: 3, nama: "Maret" },
    { id: 4, nama: "April" },
    { id: 5, nama: "Mei" },
    { id: 6, nama: "Juni" },
    { id: 7, nama: "Juli" },
    { id: 8, nama: "Agustus" },
    { id: 9, nama: "September" },
    { id: 10, nama: "Oktober" },
    { id: 11, nama: "November" },
    { id: 12, nama: "Desember" },
];

function ReportBuahAlami() {
    const [dataList, setDataList] = useState([]);
    const [allDataList, setAllDataList] = useState([]); // Simpan semua data untuk filtering
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(0); // 0 = Chart, 1 = Tabel

    // State untuk filter bulan dan tahun
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default bulan saat ini
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); // Default tahun saat ini

    // State untuk menu dropdown
    const [monthAnchorEl, setMonthAnchorEl] = useState(null);
    const [yearAnchorEl, setYearAnchorEl] = useState(null);

    // State untuk data lokasi tambahan
    const [lokasiData, setLokasiData] = useState([]);

    // State untuk cache Real Forcing dan Tanggal Closing
    const [realForcingCache, setRealForcingCache] = useState({});
    const [tanggalClosingCache, setTanggalClosingCache] = useState({});

    // PERBAIKAN 2: Ref untuk menyimpan data sebelumnya untuk mencegah kedipan
    const prevDataListRef = useRef([]);
    const prevFilterRef = useRef({ month: selectedMonth, year: selectedYear });

    // Generate daftar tahun (dari 2020 sampai tahun depan)
    const currentYear = new Date().getFullYear();
    const yearList = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i).filter((year) => year >= 2020);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Handler untuk bulan dropdown
    const handleMonthClick = (event) => {
        setMonthAnchorEl(event.currentTarget);
    };

    const handleMonthClose = () => {
        setMonthAnchorEl(null);
    };

    const handleMonthSelect = (monthId) => {
        // PERBAIKAN 2: Simpan data sebelum filter
        prevDataListRef.current = [...dataList];
        prevFilterRef.current = { month: selectedMonth, year: selectedYear };
        
        setSelectedMonth(monthId);
        handleMonthClose();
    };

    // Handler untuk tahun dropdown
    const handleYearClick = (event) => {
        setYearAnchorEl(event.currentTarget);
    };

    const handleYearClose = () => {
        setYearAnchorEl(null);
    };

    const handleYearSelect = (year) => {
        // PERBAIKAN 2: Simpan data sebelum filter
        prevDataListRef.current = [...dataList];
        prevFilterRef.current = { month: selectedMonth, year: selectedYear };
        
        setSelectedYear(year);
        handleYearClose();
    };

    // Fungsi untuk mengambil Real Forcing dari Setara Urea Per Umur
    const fetchRealForcingFromSetaraUrea = async (id_lokasi) => {
        // Cek cache dulu
        if (realForcingCache[id_lokasi] !== undefined) {
            return realForcingCache[id_lokasi];
        }
        
        // Jika tidak ada di cache, fetch dari Firebase
        const realForcing = await getRealForcingFromSetaraUrea(id_lokasi, database, onValuePromise);
        
        // Update cache (simpan null jika tidak ditemukan)
        setRealForcingCache(prev => ({
            ...prev,
            [id_lokasi]: realForcing
        }));
        
        return realForcing;
    };

    // Fungsi untuk mengambil Tanggal Closing dari Monitor Hasil Panen
    const fetchTanggalClosingFromMonitorHasilPanen = async (id_lokasi) => {
        // Cek cache dulu
        if (tanggalClosingCache[id_lokasi] !== undefined) {
            return tanggalClosingCache[id_lokasi];
        }
        
        // Jika tidak ada di cache, fetch dari Firebase
        const tanggalClosing = await getTanggalClosingFromMonitorHasilPanen(id_lokasi, database, onValuePromise);
        
        // Update cache (simpan null jika tidak ditemukan)
        setTanggalClosingCache(prev => ({
            ...prev,
            [id_lokasi]: tanggalClosing
        }));
        
        return tanggalClosing;
    };

    // Filter data berdasarkan bulan dan tahun yang dipilih
    useEffect(() => {
        console.log("🔍 Filter changed:", { selectedMonth, selectedYear });
        console.log("📊 All data count:", allDataList.length);
        
        if (allDataList.length > 0) {
            // Filter berdasarkan bulan dan tahun
            const filteredData = allDataList.filter((item) => {
                if (!item.tanggal_parsed) {
                    console.warn("⚠️ Item without tanggal_parsed:", item);
                    return false;
                }
                const matches = item.tanggal_parsed.month === selectedMonth && item.tanggal_parsed.year === selectedYear;
                return matches;
            });

            console.log("✅ Filtered data count:", filteredData.length);
            
            // Debug: Tampilkan detail setiap item yang difilter
            if (filteredData.length > 0) {
                console.log("📋 Filtered data details:");
                filteredData.forEach((item, index) => {
                    console.log(`  ${index + 1}. ${item.lokasi} - ${item.tanggal_terbang} - Early: ${item.early_cones}, Dry: ${item.dry_petals}, Buah: ${item.buah_alami}`);
                });
            }

            // Sort berdasarkan tanggal (terbaru dulu)
            filteredData.sort((a, b) => {
                try {
                    const dateA = new Date(a.tanggal_parsed.year, a.tanggal_parsed.month - 1, a.tanggal_parsed.day);
                    const dateB = new Date(b.tanggal_parsed.year, b.tanggal_parsed.month - 1, b.tanggal_parsed.day);
                    return dateB - dateA; // Descending (terbaru dulu)
                } catch (e) {
                    console.error("Error sorting dates:", e);
                    return 0;
                }
            });

            // Pastikan data memiliki field yang dibutuhkan chart
            const enhancedFilteredData = filteredData.map(item => {
                // Parse nilai numerik dengan lebih hati-hati
                const earlyConesNumeric = parseNumberWithComma(item.early_cones);
                const dryPetalsNumeric = parseNumberWithComma(item.dry_petals);
                const buahAlamiNumeric = parseNumberWithComma(item.buah_alami);
                
                // Parse nilai persentase untuk chart persentase
                const percentageEarlyConesNumeric = parsePercentageToNumber(item.percentage_by_populasi_early_cones);
                const percentageDryPetalsNumeric = parsePercentageToNumber(item.percentage_by_populasi_dry_petals);
                const percentageBuahAlamiNumeric = parsePercentageToNumber(item.percentage_by_populasi_buah_alami);
                
                console.log(`🔢 Parsed values for ${item.lokasi}:`, {
                    earlyCones: { original: item.early_cones, parsed: earlyConesNumeric },
                    dryPetals: { original: item.dry_petals, parsed: dryPetalsNumeric },
                    buahAlami: { original: item.buah_alami, parsed: buahAlamiNumeric },
                    percentageEarlyCones: { original: item.percentage_by_populasi_early_cones, parsed: percentageEarlyConesNumeric },
                    percentageDryPetals: { original: item.percentage_by_populasi_dry_petals, parsed: percentageDryPetalsNumeric },
                    percentageBuahAlami: { original: item.percentage_by_populasi_buah_alami, parsed: percentageBuahAlamiNumeric }
                });

                return {
                    ...item,
                    // Pastikan nilai-nilai untuk chart dalam format yang benar
                    early_cones_numeric: earlyConesNumeric,
                    dry_petals_numeric: dryPetalsNumeric,
                    buah_alami_numeric: buahAlamiNumeric,
                    // Nilai persentase untuk chart persentase
                    percentage_early_cones_numeric: percentageEarlyConesNumeric,
                    percentage_dry_petals_numeric: percentageDryPetalsNumeric,
                    percentage_buah_alami_numeric: percentageBuahAlamiNumeric,
                    // Format umur HSF untuk chart
                    umur_hsf_saat_ini_formatted: item.umur_hsf_saat_ini_formatted || 
                        (item.umur_hsf_saat_ini ? formatDecimal(parseFloat(item.umur_hsf_saat_ini)) : '-')
                };
            });

            // Validasi data sebelum di-set
            console.log("📈 Final data for chart:", {
                count: enhancedFilteredData.length,
                lokasi: enhancedFilteredData.map(item => item.lokasi),
                earlyConesData: enhancedFilteredData.map(item => item.early_cones_numeric),
                dryPetalsData: enhancedFilteredData.map(item => item.dry_petals_numeric),
                buahAlamiData: enhancedFilteredData.map(item => item.buah_alami_numeric),
                percentageEarlyConesData: enhancedFilteredData.map(item => item.percentage_early_cones_numeric),
                percentageDryPetalsData: enhancedFilteredData.map(item => item.percentage_dry_petals_numeric),
                percentageBuahAlamiData: enhancedFilteredData.map(item => item.percentage_buah_alami_numeric)
            });

            // Cek duplikat lokasi
            const lokasiCount = {};
            enhancedFilteredData.forEach(item => {
                lokasiCount[item.lokasi] = (lokasiCount[item.lokasi] || 0) + 1;
            });
            
            const duplicateLokasi = Object.keys(lokasiCount).filter(lokasi => lokasiCount[lokasi] > 1);
            if (duplicateLokasi.length > 0) {
                console.warn("⚠️ Found duplicate lokasi in data:", duplicateLokasi);
                console.log("   Total unique lokasi:", Object.keys(lokasiCount).length);
                console.log("   Total data items:", enhancedFilteredData.length);
            }

            // PERBAIKAN 2: Bandingkan dengan data sebelumnya
            const isSameData = prevDataListRef.current.length === enhancedFilteredData.length &&
                prevDataListRef.current.every((item, idx) => 
                    item.firebaseKey === enhancedFilteredData[idx]?.firebaseKey &&
                    item.early_cones_numeric === enhancedFilteredData[idx]?.early_cones_numeric
                );
            
            if (!isSameData) {
                console.log("🔄 Data changed, updating state...");
                setDataList(enhancedFilteredData);
            } else {
                console.log("⚡ Data unchanged, skipping state update to prevent flicker");
            }
        } else {
            console.log("📭 No allDataList available for filtering");
            setDataList([]);
        }
    }, [selectedMonth, selectedYear, allDataList]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                console.log("🚀 Starting data fetch...");
                
                // 1. Fetch data drone buah alami
                const droneRef = ref(database, "db_drone/tb_buah_alami");
                const droneSnapshot = await onValuePromise(droneRef);
                const droneData = droneSnapshot.val();

                console.log("📡 Drone data received:", droneData ? Object.keys(droneData).length : 0, "records");

                if (!droneData) {
                    console.log("❌ No drone data found");
                    setDataList([]);
                    setAllDataList([]);
                    setLoading(false);
                    return;
                }

                // 2. Fetch data lokasi untuk mendapatkan informasi tambahan
                const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
                const lokasiSnapshot = await onValuePromise(lokasiRef);
                const lokasiRawData = lokasiSnapshot.val();

                let lokasiArray = [];
                if (lokasiRawData) {
                    lokasiArray = Object.keys(lokasiRawData).map((key) => ({
                        firebaseKey: key,
                        ...lokasiRawData[key],
                    }));
                    setLokasiData(lokasiArray);
                    console.log("📍 Lokasi data:", lokasiArray.length, "records");
                }

                // 3. Process semua data
                const allProcessedData = [];
                const processedData = [];

                Object.keys(droneData).forEach((key, index) => {
                    const record = droneData[key];
                    
                    console.log(`📝 Processing record ${index + 1}/${Object.keys(droneData).length}:`, {
                        key,
                        tanggal_terbang: record.tanggal_terbang,
                        id_lokasi: record.id_lokasi
                    });

                    // Cek tanggal terbang
                    const tanggalTerbang = record.tanggal_terbang;
                    if (!tanggalTerbang || tanggalTerbang === "-") {
                        console.log(`   ⚠️ Skipping - no tanggal terbang`);
                        return;
                    }

                    // Parse tanggal
                    const parsedDate = parseTanggalTerbang(tanggalTerbang);
                    if (!parsedDate) {
                        console.log(`   ⚠️ Skipping - invalid date format: ${tanggalTerbang}`);
                        return;
                    }

                    // Cari info lokasi tambahan
                    const lokasiInfo = findLokasiById(lokasiArray, record.id_lokasi);

                    // Format tanggal untuk display (gunakan format asli)
                    const displayDate = tanggalTerbang;

                    const dataItem = {
                        firebaseKey: key,
                        id_lokasi: record.id_lokasi || "",
                        lokasi: lokasiInfo?.lokasi || record.id_lokasi || "-",
                        tanggal_terbang: displayDate,
                        tanggal_parsed: parsedDate,
                        early_cones: record.early_cones || "-",
                        dry_petals: record.dry_petals || "-",
                        buah_alami: record.buah_alami || "-",
                        populasi_per_ha: record.populasi_per_ha || "-",
                        // Ambil data dari lokasiInfo jika ada
                        luas_netto: lokasiInfo?.netto || "-",
                        mulai_rawat: lokasiInfo?.mulai_rawat || "-", // Ambil mulai_rawat untuk perhitungan umur terbang
                    };

                    console.log(`   ✅ Added to allProcessedData:`, {
                        lokasi: dataItem.lokasi,
                        tanggal: dataItem.tanggal_terbang,
                        month: parsedDate.month,
                        year: parsedDate.year
                    });

                    // Simpan semua data
                    allProcessedData.push(dataItem);

                    // Filter untuk bulan dan tahun default (saat ini)
                    if (parsedDate.month === selectedMonth && parsedDate.year === selectedYear) {
                        processedData.push(dataItem);
                        console.log(`   ✅ Also added to processedData (matches default filter)`);
                    }
                });

                console.log("📊 Processed data counts:", {
                    allProcessedData: allProcessedData.length,
                    processedData: processedData.length
                });

                // 4. Tambahkan perhitungan untuk setiap item
                const enhanceDataWithCalculations = async (dataArray) => {
                    console.log(`🔄 Enhancing ${dataArray.length} records with calculations...`);
                    
                    const enhancedData = await Promise.all(dataArray.map(async (item, index) => {
                        try {
                            console.log(`   🔄 Enhancing ${index + 1}/${dataArray.length}: ${item.lokasi}`);
                            
                            // Ambil Real Forcing dan Tanggal Closing
                            const [realForcing, tanggalClosing] = await Promise.all([
                                fetchRealForcingFromSetaraUrea(item.id_lokasi),
                                fetchTanggalClosingFromMonitorHasilPanen(item.id_lokasi)
                            ]);

                            console.log(`   📊 For ${item.lokasi}:`, {
                                realForcing,
                                tanggalClosing
                            });

                            // Hitung Umur (HSF) Saat Ini
                            const umurHSFSaatIni = calculateUmurHSFSaatIni(
                                realForcing,
                                tanggalClosing,
                                new Date()
                            );

                            // Format umur_hsf_saat_ini untuk konsistensi antara chart dan tabel
                            const formattedUmurHSF = umurHSFSaatIni && umurHSFSaatIni !== '-' 
                                ? formatDecimal(parseFloat(umurHSFSaatIni)) 
                                : umurHSFSaatIni || '-';

                            // Hitung Umur Terbang jika mulai_rawat tersedia
                            let umurTerbang = { hari: '-', bulan: '-', display: '-' };
                            if (item.mulai_rawat && item.mulai_rawat !== '-' && item.tanggal_terbang && item.tanggal_terbang !== '-') {
                                umurTerbang = calculateUmurTerbang(item.mulai_rawat, item.tanggal_terbang);
                            }

                            // Hitung semua formula menggunakan calculateAllFormulas
                            const calculations = calculateAllFormulas({
                                tanggal_terbang: item.tanggal_terbang,
                                early_cones: item.early_cones,
                                dry_petals: item.dry_petals,
                                buah_alami: item.buah_alami,
                                populasi_per_ha: item.populasi_per_ha,
                                luas_netto: item.luas_netto
                            }, item.mulai_rawat);

                            return {
                                ...item,
                                real_forcing: realForcing || "-",
                                tanggal_closing: tanggalClosing || "-",
                                umur_hsf_saat_ini: umurHSFSaatIni,
                                umur_hsf_saat_ini_formatted: formattedUmurHSF,
                                // Data Umur Terbang
                                umur_terbang_hari: umurTerbang.hari,
                                umur_terbang_bulan: umurTerbang.bulan,
                                umur_terbang_display: umurTerbang.display,
                                // Data hasil perhitungan dari calculateAllFormulas
                                total_buah_alami: calculations.totalBuahAlami,
                                populasi_total: calculations.populasiTotal,
                                percentage_by_populasi_early_cones: calculations.percentagesByPopulasi.earlyConesPercentage,
                                percentage_by_populasi_dry_petals: calculations.percentagesByPopulasi.dryPetalsPercentage,
                                percentage_by_populasi_buah_alami: calculations.percentagesByPopulasi.buahAlamiPercentage,
                                percentage_by_populasi_total: calculations.percentagesByPopulasi.totalPercentage,
                                // Pastikan nilai numerik sudah di-parse
                                early_cones_numeric: parseNumberWithComma(item.early_cones),
                                dry_petals_numeric: parseNumberWithComma(item.dry_petals),
                                buah_alami_numeric: parseNumberWithComma(item.buah_alami),
                                // Tambahkan identifier unik untuk chart
                                chartIdentifier: `${item.lokasi}_${item.tanggal_terbang}_${item.id_lokasi}`
                            };
                        } catch (error) {
                            console.error("❌ Error calculating for item:", item.id_lokasi, error);
                            return {
                                ...item,
                                real_forcing: "-",
                                tanggal_closing: "-",
                                umur_hsf_saat_ini: "-",
                                umur_hsf_saat_ini_formatted: "-",
                                umur_terbang_hari: "-",
                                umur_terbang_bulan: "-",
                                umur_terbang_display: "-",
                                total_buah_alami: "-",
                                populasi_total: "-",
                                percentage_by_populasi_early_cones: "-",
                                percentage_by_populasi_dry_petals: "-",
                                percentage_by_populasi_buah_alami: "-",
                                percentage_by_populasi_total: "-",
                                early_cones_numeric: parseNumberWithComma(item.early_cones),
                                dry_petals_numeric: parseNumberWithComma(item.dry_petals),
                                buah_alami_numeric: parseNumberWithComma(item.buah_alami),
                                // Tambahkan identifier unik untuk chart
                                chartIdentifier: `${item.lokasi}_${item.tanggal_terbang}_${item.id_lokasi}_${Date.now()}`
                            };
                        }
                    }));
                    
                    console.log(`✅ Enhanced ${enhancedData.length} records`);
                    return enhancedData;
                };

                // Enhance semua data
                const enhancedAllData = await enhanceDataWithCalculations(allProcessedData);
                const enhancedProcessedData = await enhanceDataWithCalculations(processedData);

                // Validasi data yang di-enhance
                console.log("🔍 Validating enhanced data:");
                enhancedAllData.forEach((item, index) => {
                    if (!item.lokasi || item.lokasi === "-") {
                        console.warn(`   ⚠️ Item ${index} has invalid lokasi:`, item);
                    }
                    if (isNaN(item.early_cones_numeric) || isNaN(item.dry_petals_numeric) || isNaN(item.buah_alami_numeric)) {
                        console.warn(`   ⚠️ Item ${index} (ID: ${item.id_lokasi}) has NaN values:`, {
                            early_cones: item.early_cones,
                            early_cones_numeric: item.early_cones_numeric,
                            dry_petals: item.dry_petals,
                            dry_petals_numeric: item.dry_petals_numeric,
                            buah_alami: item.buah_alami,
                            buah_alami_numeric: item.buah_alami_numeric
                        });
                    }
                    
                    // Cek nilai yang tidak valid dan tampilkan id_lokasi
                    const invalidValues = [];
                    if (isNaN(item.early_cones_numeric) || item.early_cones_numeric === null || item.early_cones_numeric === undefined) {
                        invalidValues.push(`early_cones_numeric: ${item.early_cones_numeric}`);
                    }
                    if (isNaN(item.dry_petals_numeric) || item.dry_petals_numeric === null || item.dry_petals_numeric === undefined) {
                        invalidValues.push(`dry_petals_numeric: ${item.dry_petals_numeric}`);
                    }
                    if (isNaN(item.buah_alami_numeric) || item.buah_alami_numeric === null || item.buah_alami_numeric === undefined) {
                        invalidValues.push(`buah_alami_numeric: ${item.buah_alami_numeric}`);
                    }
                    
                    if (invalidValues.length > 0) {
                        console.error(`🚨 Data tidak valid ditemukan! ID Lokasi: ${item.id_lokasi}, Lokasi: ${item.lokasi}, Nilai tidak valid: ${invalidValues.join(', ')}`);
                    }
                });

                // Sort data default (bulan dan tahun saat ini)
                enhancedProcessedData.sort((a, b) => {
                    try {
                        const dateA = new Date(a.tanggal_parsed.year, a.tanggal_parsed.month - 1, a.tanggal_parsed.day);
                        const dateB = new Date(b.tanggal_parsed.year, b.tanggal_parsed.month - 1, b.tanggal_parsed.day);
                        return dateB - dateA; // Descending (terbaru dulu)
                    } catch (e) {
                        console.error("Error sorting processed data:", e);
                        return 0;
                    }
                });

                // Sort all data
                enhancedAllData.sort((a, b) => {
                    try {
                        const dateA = new Date(a.tanggal_parsed.year, a.tanggal_parsed.month - 1, a.tanggal_parsed.day);
                        const dateB = new Date(b.tanggal_parsed.year, b.tanggal_parsed.month - 1, b.tanggal_parsed.day);
                        return dateB - dateA;
                    } catch (e) {
                        console.error("Error sorting all data:", e);
                        return 0;
                    }
                });

                console.log("🎯 Final data counts:", {
                    enhancedProcessedData: enhancedProcessedData.length,
                    enhancedAllData: enhancedAllData.length
                });

                // PERBAIKAN 2: Inisialisasi ref dengan data awal
                prevDataListRef.current = enhancedProcessedData;
                prevFilterRef.current = { month: selectedMonth, year: selectedYear };
                
                setDataList(enhancedProcessedData);
                setAllDataList(enhancedAllData);
                setError(null);
                
                console.log("✅ Data fetch completed successfully");
            } catch (err) {
                console.error("❌ Error fetching data:", err);
                setError("Gagal memuat data report buah alami");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Dapatkan nama bulan yang dipilih
    const selectedMonthName = bulanList.find((bulan) => bulan.id === selectedMonth)?.nama || "";

    // Debug: Log data saat render
    useEffect(() => {
        if (dataList.length > 0 && activeTab === 0) { // Hanya log untuk chart tab
            console.log("🎨 Rendering BarChart with dataList:", {
                count: dataList.length,
                lokasi: dataList.map(item => item.lokasi),
                chartIdentifiers: dataList.map(item => item.chartIdentifier),
                earlyConesData: dataList.map(item => item.early_cones_numeric),
                dryPetalsData: dataList.map(item => item.dry_petals_numeric),
                buahAlamiData: dataList.map(item => item.buah_alami_numeric),
                percentageEarlyConesData: dataList.map(item => item.percentage_early_cones_numeric),
                percentageDryPetalsData: dataList.map(item => item.percentage_dry_petals_numeric),
                percentageBuahAlamiData: dataList.map(item => item.percentage_buah_alami_numeric),
                umurTerbangData: dataList.map(item => item.umur_terbang_display)
            });
            
            // Cek duplikat lokasi
            const lokasiCount = {};
            dataList.forEach(item => {
                lokasiCount[item.lokasi] = (lokasiCount[item.lokasi] || 0) + 1;
            });
            
            const duplicateLokasi = Object.keys(lokasiCount).filter(lokasi => lokasiCount[lokasi] > 1);
            if (duplicateLokasi.length > 0) {
                console.warn("⚠️ Duplicate lokasi in chart data:", duplicateLokasi);
                duplicateLokasi.forEach(lokasi => {
                    const items = dataList.filter(item => item.lokasi === lokasi);
                    console.log(`   ${lokasi}: ${items.length} entries`, items.map(item => ({
                        tanggal: item.tanggal_terbang,
                        id: item.id_lokasi,
                        chartIdentifier: item.chartIdentifier
                    })));
                });
            }
            
            // Cek apakah ada data yang tidak valid dan tampilkan id_lokasi
            const invalidData = [];
            dataList.forEach((item, index) => {
                if (item.early_cones_numeric === undefined || 
                    item.dry_petals_numeric === undefined || 
                    item.buah_alami_numeric === undefined ||
                    isNaN(item.early_cones_numeric) || 
                    isNaN(item.dry_petals_numeric) || 
                    isNaN(item.buah_alami_numeric)) {
                    
                    invalidData.push({
                        index,
                        id_lokasi: item.id_lokasi,
                        lokasi: item.lokasi,
                        early_cones_numeric: item.early_cones_numeric,
                        dry_petals_numeric: item.dry_petals_numeric,
                        buah_alami_numeric: item.buah_alami_numeric
                    });
                }
            });
            
            if (invalidData.length > 0) {
                console.error("🚨 Found invalid data in dataList!");
                invalidData.forEach(data => {
                    console.error(`   Item ${data.index} - ID Lokasi: ${data.id_lokasi}, Lokasi: ${data.lokasi} has invalid values:`, data);
                });
            }
        }
    }, [dataList, activeTab]);

    if (loading) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 2, mb: 3, boxShadow: 0 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    Report Buah Alami
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 3 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        Memuat data...
                    </Typography>
                </Box>
            </Paper>
        );
    }

    if (error) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 2, mb: 3 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                    Report Buah Alami
                </Typography>
                <Box sx={{ p: 2 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </Paper>
        );
    }

    if (dataList.length === 0) {
        return (
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Report Buah Alami {selectedMonthName} {selectedYear}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip Bulan dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleMonthClick}
                            label={selectedMonthName}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: monthAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleMonthClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Tahun dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear.toString()}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: yearAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />

                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ cursor: "pointer" }}
                            label={`${dataList.length} lokasi`}
                        />
                    </Stack>
                </Stack>

                {/* Tab Navigation */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 100,
                        border: "1px dashed #e0e0e0",
                        borderRadius: 1,
                        p: 0,
                    }}
                >
                    <Typography variant="body1" color="text.secondary">
                        Tidak ada data buah alami untuk {selectedMonthName} {selectedYear}.
                    </Typography>
                </Box>

                {/* Menu Dropdown Bulan */}
                <Menu
                    anchorEl={monthAnchorEl}
                    open={Boolean(monthAnchorEl)}
                    onClose={handleMonthClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {bulanList.map((bulan) => (
                        <MenuItem
                            key={bulan.id}
                            onClick={() => handleMonthSelect(bulan.id)}
                            selected={selectedMonth === bulan.id}
                        >
                            {bulan.nama}
                        </MenuItem>
                    ))}
                </Menu>

                {/* Menu Dropdown Tahun */}
                <Menu
                    anchorEl={yearAnchorEl}
                    open={Boolean(yearAnchorEl)}
                    onClose={handleYearClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {yearList.map((year) => (
                        <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                            {year}
                        </MenuItem>
                    ))}
                </Menu>
            </Paper>
        );
    }

    return (
        <Box>
            <Paper sx={{ width: "100%", overflow: "hidden", p: 0, boxShadow: 0, mb: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="body1" color="text.primary">
                        Report Buah Alami {selectedMonthName} {selectedYear} - {dataList.length} lokasi
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Chip Bulan dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleMonthClick}
                            label={selectedMonthName}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: monthAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleMonthClick}
                            sx={{ cursor: "pointer" }}
                        />

                        {/* Chip Tahun dengan Dropdown */}
                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            onClick={handleYearClick}
                            label={selectedYear.toString()}
                            deleteIcon={
                                <ArrowDropDownOutlinedIcon
                                    sx={{
                                        transform: yearAnchorEl ? "rotate(180deg)" : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                    }}
                                />
                            }
                            onDelete={handleYearClick}
                            sx={{ cursor: "pointer" }}
                        />

                        <Chip
                            size="small"
                            color="secondary"
                            variant="outlined"
                            sx={{ cursor: "pointer" }}
                            label={`${dataList.length} lokasi`}
                        />
                    </Stack>
                </Stack>

                {/* Tab Navigation */}
                <Box sx={{ mb: 1, mt: 1 }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 36,
                            minWidth: 72,
                            "& .MuiTabs-indicator": {
                                display: "none",
                            },
                            "& .MuiTab-root": {
                                minHeight: 36,
                                minWidth: 72,
                                py: 0,
                                px: 0,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                            },
                        }}
                    >
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Bar Chart"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 0 ? "primary" : "default"}
                                    variant={activeTab === 0 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                        <Tab
                            disableRipple
                            label={
                                <Chip
                                    label="Tabel"
                                    size="small"
                                    onClick={() => {}}
                                    color={activeTab === 1 ? "primary" : "default"}
                                    variant={activeTab === 1 ? "filled" : "outlined"}
                                    sx={{ cursor: "pointer" }}
                                />
                            }
                        />
                    </Tabs>
                </Box>

                {/* Container dengan tinggi tetap untuk kedua tab */}
                <Box>
                    {/* Chart Tab Content */}
                    {activeTab === 0 && (
                        <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                            <Grid size={12}>
                                <Box
                                    sx={{
                                        height: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Render Bar Chart langsung di sini */}
                                    {dataList.length === 0 ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Tidak ada data untuk ditampilkan
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ height: "100%", width: "100%", p: 2 }}>
                                            {/* Debug info */}
                                            <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 1 }}>
                                                <Chip 
                                                    size="small" 
                                                    color="info" 
                                                    variant="outlined" 
                                                    label={`${dataList.length} data`}
                                                />
                                            </Box>
                                            
                                            {/* Hitung nilai maksimum untuk yAxis chart absolut */}
                                            {(() => {
                                                // Fungsi untuk menghitung nilai maksimum dari data absolut
                                                const calculateMaxAbsolute = () => {
                                                    if (dataList.length === 0) return 100;
                                                    
                                                    let maxAbsolute = 0;
                                                    
                                                    dataList.forEach(item => {
                                                        // Cek nilai absolut dari ketiga kategori
                                                        const earlyCones = item.early_cones_numeric || 0;
                                                        const dryPetals = item.dry_petals_numeric || 0;
                                                        const buahAlami = item.buah_alami_numeric || 0;
                                                        
                                                        maxAbsolute = Math.max(
                                                            maxAbsolute, 
                                                            earlyCones, 
                                                            dryPetals, 
                                                            buahAlami
                                                        );
                                                    });
                                                    
                                                    // Tambahkan 10% dari nilai maksimum untuk memberi ruang
                                                    const adjustedMax = Math.ceil(maxAbsolute * 1.1);
                                                    
                                                    // Pastikan nilai minimum untuk kasus data sangat kecil
                                                    return Math.max(adjustedMax, 10);
                                                };
                                                
                                                const maxYValue = calculateMaxAbsolute();
                                                console.log(`📊 Calculated max Y value for absolute chart: ${maxYValue}`);
                                                
                                                return (
<BarChart
    key={`chart-absolut-${selectedMonth}-${selectedYear}-${dataList.length}-${maxYValue}`}
    xAxis={[
        {
            
            data: dataList.map((item, index) => {
                const umurHSF = item.umur_hsf_saat_ini_formatted || "-";
                // Mengambil umur terbang
                const umurTerbang = item.umur_terbang_bulan || "-";
                
                // Logika identifikasi duplikat lokasi
                const lokasiCount = dataList
                    .slice(0, index + 1)
                    .filter(i => i.lokasi === item.lokasi).length;
                
                // FORMAT LABEL: Lokasi, Umur HSF, dan Umur Terbang
                const baseLabel = lokasiCount > 1 ? `${item.lokasi} #${lokasiCount}` : item.lokasi;
                
                // Gunakan \n untuk baris baru (pastikan sx whiteSpace: "pre-line" aktif)
                return `${baseLabel}\n(${umurHSF} HSF)\n(TB: ${umurTerbang})`;
            }),
            scaleType: "band",
            tickPlacement: 'middle',
            tickLabelPlacement: 'middle',
        },
    ]}
    yAxis={[{ 
        min: 0,
        max: maxYValue
    }]}
    series={[
        { 
            data: dataList.map((item) => item.early_cones_numeric || 0), 
            label: "Early Cones", 
            color: "#f44336",
            valueFormatter: (value) => `${formatNumberForTooltip(value)}`,
        },
        { 
            data: dataList.map((item) => item.dry_petals_numeric || 0), 
            label: "Dry Petals", 
            color: "#ffeb3b",
            valueFormatter: (value) => `${formatNumberForTooltip(value)}`,
        },
        { 
            data: dataList.map((item) => item.buah_alami_numeric || 0), 
            label: "Buah", 
            color: "#4caf50",
            valueFormatter: (value) => `${formatNumberForTooltip(value)}`,
        },
    ]}
    // TAMBAHAN: Perbesar margin bottom agar teks 3 baris tidak terpotong
    margin={{ top: 10, right: 10, bottom: 80, left: 0 }}
    slotProps={{
        legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "right" },
            labelStyle: { fontSize: 11 },
            padding: { top: 10 }
        },
    }}
    height={350} // Sedikit ditingkatkan agar proporsional dengan margin baru
    sx={{
        "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
            whiteSpace: "pre-line", // Penting agar \n berfungsi
            fontSize: 9,           // Ukuran font sedikit diperkecil agar pas
            textAlign: "center",
        },
        transition: 'all 0.3s ease-in-out',
    }}
/>
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>

                            <Grid size={12}>
                                {/* Chart menggunakan data % */}
                                <Box
                                    sx={{
                                        height: 350,
                                        border: "1px solid #e0e0e0",
                                        borderRadius: 1,
                                        bgcolor: "background.paper",
                                        overflow: "hidden",
                                    }}
                                >
                                    {/* Render Bar Chart untuk persentase */}
                                    {dataList.length === 0 ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
                                            <Typography variant="body2" color="text.secondary">
                                                Tidak ada data persentase untuk ditampilkan
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box sx={{ height: "100%", width: "100%", p: 2 }}>
                                            {/* Debug info */}
                                            <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 1 }}>
                                                <Chip 
                                                    size="small" 
                                                    color="info" 
                                                    variant="outlined" 
                                                    label={`${dataList.length} data`}
                                                />
                                            </Box>
                                            
                                            {/* Hitung nilai maksimum untuk yAxis chart persentase */}
                                            {(() => {
                                                // Fungsi untuk menghitung nilai maksimum dari data persentase
                                                const calculateMaxPercentage = () => {
                                                    if (dataList.length === 0) return 100;
                                                    
                                                    let maxPercentage = 0;
                                                    
                                                    dataList.forEach(item => {
                                                        // Cek nilai persentase dari ketiga kategori
                                                        const earlyCones = item.percentage_early_cones_numeric || 0;
                                                        const dryPetals = item.percentage_dry_petals_numeric || 0;
                                                        const buahAlami = item.percentage_buah_alami_numeric || 0;
                                                        
                                                        maxPercentage = Math.max(
                                                            maxPercentage, 
                                                            earlyCones, 
                                                            dryPetals, 
                                                            buahAlami
                                                        );
                                                    });
                                                    
                                                    // Tambahkan 10 untuk memberi ruang dan bulatkan ke atas
                                                    const adjustedMax = Math.ceil(maxPercentage + 10);
                                                    
                                                    // Pastikan tidak kurang dari 20% untuk kasus data sangat kecil
                                                    return Math.max(adjustedMax, 20);
                                                };
                                                
                                                const maxYValue = calculateMaxPercentage();
                                                console.log(`📊 Calculated max Y value for percentage chart: ${maxYValue}%`);
                                                
                                                return (
<BarChart
    key={`chart-persentase-${selectedMonth}-${selectedYear}-${dataList.length}-${maxYValue}`}
    xAxis={[
        {
            data: dataList.map((item, index) => {
                const umurHSF = item.umur_hsf_saat_ini_formatted || "-";
                const umurTerbang = item.umur_terbang_bulan || "-";
                
                // Logika identifikasi duplikat lokasi
                const lokasiCount = dataList
                    .slice(0, index + 1)
                    .filter(i => i.lokasi === item.lokasi).length;
                
                // Format label 3 baris
                const baseLabel = lokasiCount > 1 ? `${item.lokasi} #${lokasiCount}` : item.lokasi;
                
                return `${baseLabel}\n(${umurHSF} HSF)\n(TB: ${umurTerbang})`;
            }),
            scaleType: "band",
            tickPlacement: 'middle',
            tickLabelPlacement: 'middle',
        },
    ]}
    yAxis={[{ 
        min: 0,
        max: 100, // Biasanya persentase max di 100
        valueFormatter: (value) => `${value}%`
    }]}
    series={[
        { 
            data: dataList.map((item) => item.percentage_early_cones_numeric || 0), 
            label: "% Early Cones", 
            color: "#f44336",
            valueFormatter: (value) => `${formatPercentageForTooltip(value)}%`,
        },
        { 
            data: dataList.map((item) => item.percentage_dry_petals_numeric || 0), 
            label: "% Dry Petals", 
            color: "#ffeb3b",
            valueFormatter: (value) => `${formatPercentageForTooltip(value)}%`,
        },
        { 
            data: dataList.map((item) => item.percentage_buah_alami_numeric || 0), 
            label: "% Buah", 
            color: "#4caf50",
            valueFormatter: (value) => `${formatPercentageForTooltip(value)}%`,
        },
    ]}
    // PERBAIKAN: Margin bottom dinaikkan agar label 3 baris terlihat jelas
    margin={{ top: 10, right: 10, bottom: 80, left: 0 }}
    slotProps={{
        legend: {
            direction: "row",
            position: { vertical: "top", horizontal: "right" },
            labelStyle: { fontSize: 11 },
            padding: { top: 10 }
        },
    }}
    height={320} // Ditingkatkan sedikit agar area chart tetap lega
    sx={{
        "& .MuiChartsAxis-bottom .MuiChartsAxis-tickLabel": {
            whiteSpace: "pre-line", 
            fontSize: 9, // Ukuran font diperkecil agar tidak bertumpukan antar bar
            textAlign: "center",
        },
        transition: 'all 0.3s ease-in-out',
    }}
/>
                                                );
                                            })()}
                                        </Box>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    {/* Table Tab Content */}
                    {activeTab === 1 && (
                        <Box
                            sx={{
                                height: 350,
                                border: "1px solid #e0e0e0",
                                borderRadius: 1,
                                bgcolor: "background.paper",
                                overflow: "hidden",
                            }}
                        >
                            <Box sx={{ height: "100%", width: "100%" }}>
                                {/* Debug info */}
                                <Box sx={{ position: 'absolute', top: 5, right: 5, zIndex: 1 }}>
                                    <Chip 
                                        size="small" 
                                        color="info" 
                                        variant="outlined" 
                                        label={`${dataList.length} rows`}
                                    />
                                </Box>
                                
                                {/* Gunakan CustomTableContainer di sini */}
                                <CustomTableContainer
                                    sx={{
                                        height: "100%", // Isi penuh container
                                        border: "none", // Hapus border karena sudah ada di parent
                                        borderRadius: 1,
                                    }}
                                    hoverScrollbar={true}
                                    scrollbarHeight="8px"
                                    scrollbarWidth="8px"
                                    scrollbarThumbColor="#c1c1c1"
                                    scrollbarHoverColor="#a8a8a8"
                                >
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow className="tr">
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    No.
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Tanggal Terbang
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Lokasi
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Luas Netto
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Umur (HSF) Saat Ini
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Umur Terbang
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Early Cones
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Dry Petals
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Buah
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    % Early Cones
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    % Dry Petals
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    % Buah
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Populasi Per Ha
                                                </TableCell>
                                                <TableCell className="ded-tch" sx={{ padding: "8px 16px" }}>
                                                    Populasi Total
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dataList.map((item, index) => (
                                                <TableRow key={`${item.firebaseKey}-${index}`} hover>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatDateDisplay(item.tanggal_terbang)}
                                                    </TableCell>
<TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
    <MapViewTooltip
        database={database}
        id_lokasi={item.id_lokasi}
        lokasiNama={item.lokasi}
        firebaseKey={item.firebaseKey}
    >
        <Box>
            <div style={{ fontWeight: "500" }}>{formatText(item.lokasi)}</div>
            <div style={{ fontSize: "0.75em", color: "#666" }}>
                ID: {item.id_lokasi || "-"}
            </div>
            
        </Box>
    </MapViewTooltip>
</TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatText(item.luas_netto)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {item.umur_hsf_saat_ini_formatted || '-'}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {item.umur_terbang_display || '-'}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatNumber(item.early_cones)}
                                                        {/* Tampilkan informasi jika data tidak valid */}
                                                        {isNaN(item.early_cones_numeric) && (
                                                            <div style={{ fontSize: "0.75em", color: "#f44336", fontWeight: "bold" }}>
                                                                ⚠️ Invalid data (ID: {item.id_lokasi})
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatNumber(item.dry_petals)}
                                                        {/* Tampilkan informasi jika data tidak valid */}
                                                        {isNaN(item.dry_petals_numeric) && (
                                                            <div style={{ fontSize: "0.75em", color: "#f44336", fontWeight: "bold" }}>
                                                                ⚠️ Invalid data (ID: {item.id_lokasi})
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatNumber(item.buah_alami)}
                                                        {/* Tampilkan informasi jika data tidak valid */}
                                                        {isNaN(item.buah_alami_numeric) && (
                                                            <div style={{ fontSize: "0.75em", color: "#f44336", fontWeight: "bold" }}>
                                                                ⚠️ Invalid data (ID: {item.id_lokasi})
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatText(item.percentage_by_populasi_early_cones)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatText(item.percentage_by_populasi_dry_petals)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatText(item.percentage_by_populasi_buah_alami)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatNumber(item.populasi_per_ha)}
                                                    </TableCell>
                                                    <TableCell className="ded-tbtrtc" sx={{ padding: "8px 16px" }}>
                                                        {formatNumber(item.populasi_total)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </CustomTableContainer>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Menu Dropdown Bulan */}
                <Menu
                    anchorEl={monthAnchorEl}
                    open={Boolean(monthAnchorEl)}
                    onClose={handleMonthClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {bulanList.map((bulan) => (
                        <MenuItem
                            key={bulan.id}
                            onClick={() => handleMonthSelect(bulan.id)}
                            selected={selectedMonth === bulan.id}
                        >
                            {bulan.nama}
                        </MenuItem>
                    ))}
                </Menu>

                {/* Menu Dropdown Tahun */}
                <Menu
                    anchorEl={yearAnchorEl}
                    open={Boolean(yearAnchorEl)}
                    onClose={handleYearClose}
                    PaperProps={{
                        style: {
                            maxHeight: 300,
                        },
                    }}
                >
                    {yearList.map((year) => (
                        <MenuItem key={year} onClick={() => handleYearSelect(year)} selected={selectedYear === year}>
                            {year}
                        </MenuItem>
                    ))}
                </Menu>

            </Paper>
        </Box>
    );
}

export default ReportBuahAlami;